import { ErrorMessage } from '@hookform/error-message';
import { type FC, useContext, useEffect, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { PersonCircle } from 'react-bootstrap-icons';

import DatePicker from 'react-datepicker';
import Select from 'react-dropdown-select';
import { Controller, useFormContext } from 'react-hook-form';

import 'react-datepicker/dist/react-datepicker.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { DateTime } from 'luxon';
import { config } from '../../../modules/config';
import { GuestPortalContext } from '../../../modules/context/guestPortalContextV2';

const inputRegExpValidation = /^(?=.*\p{L})[\p{L}\s\-.']+$/u;

interface Option {
  name: string;
}

interface UserDetailsModalProps {
  show: boolean;
  handleClose: () => void;
  isEnd?: React.Dispatch<React.SetStateAction<boolean>>;
  howDidYouHearAboutUsOptions?: Option[];
  minimalAge?: number;
}

export const UserDetailsModal: FC<UserDetailsModalProps> = ({
  show,
  handleClose,
  isEnd,
  howDidYouHearAboutUsOptions = [],
  minimalAge,
}) => {
  const {
    control,
    register,
    trigger,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useFormContext();
  const { dynamicQuestionsData: dynamicQuestions } = useContext(GuestPortalContext)!;
  const [savedAnswers, setSavedAnswers] = useState(null);

  useEffect(() => {
    if (show && savedAnswers) {
      setValue('dynamicQuestions', savedAnswers);
    }
  }, [show, savedAnswers]);

  const onClickSubmit = async () => {
    const validated = await trigger([
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'birthDate',
      'licensePlate',
      'dynamicQuestions',
    ]);

    setSavedAnswers(getValues('dynamicQuestions'));

    if (isEnd && validated) {
      isEnd(true);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      dialogClassName="post-editor-modal modal-200w"
      backdrop="static"
      id="guest-portal"
    >
      <Modal.Header className="guest-modal-header ">
        <Modal.Title className="editor-title">
          <PersonCircle size={25} /> {'  '}
          Enter your Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="guest-modal-header">
        <Form>
          <Row>
            <Col>
              <Form.Label className="editor-title-label">First Name</Form.Label>
              <Form.Control
                className="editor-control guest-inputs"
                placeholder="Enter your First name..."
                {...register('firstName', {
                  required: 'This is required.',
                  pattern: {
                    value: inputRegExpValidation,
                    message:
                      'Only letters, spaces, hyphens, and apostrophes are allowed, but must include at least one letter.',
                  },
                  setValueAs: (value) => value.trim(),
                })}
              />
              <ErrorMessage
                errors={errors}
                name="firstName"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Last Name</Form.Label>
              <Form.Control
                className="editor-control guest-inputs"
                placeholder="Enter your Last name..."
                {...register('lastName', {
                  required: 'This is required.',
                  pattern: {
                    value: inputRegExpValidation,
                    message:
                      'Only letters, spaces, hyphens, and apostrophes are allowed, but must include at least one letter.',
                  },
                  setValueAs: (value) => value.trim(),
                })}
              />
              <ErrorMessage
                errors={errors}
                name="lastName"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>

          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Email Address</Form.Label>
              <Form.Control
                className="editor-control guest-inputs"
                placeholder="Enter your Email..."
                {...register('email', {
                  required: 'This is required.',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Entered value does not match email format',
                  },
                })}
              />
              <ErrorMessage
                errors={errors}
                name="email"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Phone number</Form.Label>

              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: 'This is required.',
                  validate: {
                    hasMoreThanFourDigits: (value) => {
                      const digits = value.replace(/\D/g, '');
                      return digits.length > 4 || 'Enter a valid number.';
                    },
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <PhoneInput
                    country="us"
                    value={value}
                    onChange={(phoneValue) => {
                      onChange(
                        phoneValue.startsWith('+') ? phoneValue : `+${phoneValue}`,
                      );
                    }}
                    inputStyle={{ width: '100%' }}
                    countryCodeEditable={false}
                  />
                )}
              />

              <ErrorMessage
                errors={errors}
                name="phoneNumber"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>

          <Row className="mt-16px">
            <Col xs={12}>
              <Form.Label className="editor-title-label">Date of Birth</Form.Label>
            </Col>
            <Col xs={12}>
              <div className="datePicker">
                <Controller
                  control={control}
                  name="birthDate"
                  rules={{
                    required: 'This is required.',
                    validate: (value) => {
                      if (!value) return 'This is required.';

                      const birthDate = new Date(value);
                      const today = new Date();
                      const age = today.getFullYear() - birthDate.getFullYear();
                      const validAge = minimalAge || 21;
                      const monthDiff = today.getMonth() - birthDate.getMonth();
                      const dayDiff = today.getDate() - birthDate.getDate();

                      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
                        return age - 1 >= validAge
                          ? true
                          : `You must be at least ${validAge || 21} years old.`;
                      }

                      return age >= validAge
                        ? true
                        : `You must be at least ${validAge || 21} years old.`;
                    },
                  }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      wrapperClassName="datePicker"
                      placeholderText="Select date"
                      onChange={(date) => field.onChange(date)}
                      selected={field.value}
                      customInput={
                        <Form.Control
                          className="editor-control guest-inputs"
                          inputMode="none"
                        />
                      }
                      onYearChange={(date) => field.onChange(date)}
                      onMonthChange={(date) => field.onChange(date)}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                      dateFormat="MM/dd/yyyy"
                    />
                  )}
                />
              </div>
              <ErrorMessage
                errors={errors}
                name="birthDate"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>

          {config.dynamicQuestions.showQuestions ? (
            dynamicQuestions.map((question, index) => (
              <Row className="mt-16px" key={question.uuid}>
                <Col xs={12}>
                  <Form.Label className="editor-title-label">
                    {question.question}
                  </Form.Label>
                  {question.type === 'select' ? (
                    <Form.Select
                      className="editor-control guest-inputs"
                      defaultValue={question.answer}
                      {...register(`dynamicQuestions.${index}.answer` as const, {
                        required: question.isRequired ? 'This field is required' : false,
                      })}
                    >
                      <option value="">Select an option</option>
                      {question.selectableValues?.map((opt, i) => (
                        <option key={i} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </Form.Select>
                  ) : question.type === 'date' ? (
                    <Controller
                      control={control}
                      name={`dynamicQuestions.${index}.answer` as const}
                      rules={{
                        required: question.isRequired ? 'This field is required' : false,
                      }}
                      render={({ field }) => {
                        const selectedDate =
                          field.value && DateTime.fromISO(field.value).isValid
                            ? DateTime.fromISO(field.value).toJSDate()
                            : null;

                        return (
                          <DatePicker
                            selected={selectedDate}
                            onChange={(date) => {
                              if (date) {
                                const iso = DateTime.fromJSDate(date).toISODate();
                                field.onChange(iso);
                              } else {
                                field.onChange('');
                              }
                            }}
                            placeholderText="Select date"
                            wrapperClassName="datePicker"
                            customInput={
                              <Form.Control
                                className="editor-control guest-inputs w-100"
                                inputMode="none"
                              />
                            }
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            dateFormat="MM/dd/yyyy"
                          />
                        );
                      }}
                    />
                  ) : question.type === 'time' ? (
                    <Controller
                      control={control}
                      name={`dynamicQuestions.${index}.answer` as const}
                      rules={{
                        required: question.isRequired ? 'This field is required' : false,
                      }}
                      render={({ field }) => {
                        const valueAsDate =
                          field.value && /^\d{2}:\d{2}$/.test(field.value)
                            ? (() => {
                                const [hours, minutes] = field.value
                                  .split(':')
                                  .map(Number);
                                const d = new Date();
                                d.setHours(hours, minutes, 0, 0);
                                return d;
                              })()
                            : null;

                        return (
                          <DatePicker
                            selected={valueAsDate}
                            onChange={(date: Date | null) => {
                              if (date) {
                                const h = date.getHours().toString().padStart(2, '0');
                                const m = date.getMinutes().toString().padStart(2, '0');
                                field.onChange(`${h}:${m}`);
                              } else {
                                field.onChange('');
                              }
                            }}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={1}
                            timeCaption="Time"
                            dateFormat="HH:mm aa"
                            wrapperClassName="datePicker w-100"
                            customInput={
                              <Form.Control
                                className="editor-control guest-inputs w-100"
                                inputMode="none"
                              />
                            }
                          />
                        );
                      }}
                    />
                  ) : (
                    <Form.Control
                      className="editor-control guest-inputs"
                      type={question.type}
                      defaultValue={question.answer}
                      {...register(`dynamicQuestions.${index}.answer` as const, {
                        required: question.isRequired ? 'This field is required' : false,
                      })}
                    />
                  )}

                  <ErrorMessage
                    errors={errors}
                    name={`dynamicQuestions.${index}.answer`}
                    render={({ message }) => (
                      <span className="error-text">{message}</span>
                    )}
                  />
                </Col>
              </Row>
            ))
          ) : (
            <>
              <Row className="mt-16px">
                <Col>
                  <Form.Label className="editor-title-label">License Plate</Form.Label>
                  <Form.Control
                    className="editor-control guest-inputs"
                    placeholder="Enter your License plate..."
                    {...register('licensePlate')}
                  />
                  <ErrorMessage
                    errors={errors}
                    render={({ message }) => (
                      <span className="error-text">{message}</span>
                    )}
                    name="licensePlate"
                  />
                </Col>
              </Row>

              <Row className="mt-16px">
                <Col>
                  <Form.Label className="editor-title-label">
                    Estimated Time Of Arrival
                  </Form.Label>
                  <Form.Control
                    className="editor-control guest-inputs"
                    placeholder="Enter your Estimated Time Of Arrival..."
                    {...register('estimatedTimeOfArrival')}
                  />
                </Col>
              </Row>

              <Row className="mt-16px">
                <Col>
                  <Form.Label className="editor-title-label">
                    How did you hear about us?
                  </Form.Label>
                  <Controller
                    name="howDidYouHearAboutUs"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={howDidYouHearAboutUsOptions}
                        className="editor-control guest-inputs"
                        placeholder="Select an option..."
                        onChange={([selected]) => {
                          if (selected) {
                            field.onChange(selected.name);
                          }
                        }}
                        values={field.value ? [{ name: field.value }] : []}
                        valueField="name"
                        labelField="name"
                      />
                    )}
                  />
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal.Body>
      <Container className="guest-modal-footer ">
        <Button className="cancel-button no-border-radius" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className="save-button no-border-radius"
          style={{ marginLeft: '10px' }}
          onClick={onClickSubmit}
        >
          Continue
        </Button>
      </Container>
    </Modal>
  );
};
